import { useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemText,
  ListSubheader,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { providerLabel } from "../../utils/textUtils";
import PpLogo from "./PpLogo";
import { PpAccountOption } from "./DataFilter";

const PROVIDER_ORDER = ["stripe", "shopify", "paypal", "adyen"];
const providerRank = (ppType: string) => {
  const i = PROVIDER_ORDER.indexOf(ppType);
  return i === -1 ? PROVIDER_ORDER.length : i;
};

interface PpAccountMultiSelectFilterProps {
  label: string;
  options: PpAccountOption[];
  value: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  width?: number;
}

// PpLogo renders a fixed 20px SVG; flexShrink:0 keeps it from being squeezed
// (and visually resized) when the sibling label is long.
const Logo = ({ ppType }: { ppType: string }) => (
  <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
    <PpLogo ppType={ppType} />
  </Box>
);

export default function PpAccountMultiSelectFilter({
  label,
  options,
  value,
  onChange,
  placeholder = "All",
  searchPlaceholder = "Search…",
  width = 220,
}: PpAccountMultiSelectFilterProps) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  // Snapshot of the selection when the menu opened, so the "selected first"
  // ordering stays stable while open (rows don't jump when you toggle them).
  const [pinnedSelected, setPinnedSelected] = useState<string[]>([]);
  const clearable = value.length > 0;
  const labelId = `${label.replace(/\s+/g, "-").toLowerCase()}-pp-account-multi-label`;
  const optByValue = useMemo(() => new Map(options.map((o) => [o.value, o])), [options]);

  const sortedOptions = useMemo(
    () => [...options].sort((a, b) => providerRank(a.ppType) - providerRank(b.ppType) || a.label.localeCompare(b.label)),
    [options]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q
      ? sortedOptions.filter((o) => o.label.toLowerCase().includes(q) || providerLabel(o.ppType).toLowerCase().includes(q))
      : sortedOptions;
  }, [sortedOptions, query]);

  const pinnedSet = useMemo(() => new Set(pinnedSelected), [pinnedSelected]);
  const selectedOpts = filtered.filter((o) => pinnedSet.has(o.value));
  const restOpts = filtered.filter((o) => !pinnedSet.has(o.value));

  const handleOpen = () => {
    setPinnedSelected(value);
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
    setQuery("");
  };

  const renderOption = (opt: PpAccountOption) => (
    <MenuItem key={opt.value} value={opt.value} sx={{ pl: 1 }}>
      <Checkbox checked={value.includes(opt.value)} size="small" sx={{ mr: 1, p: 0.5 }} />
      <Logo ppType={opt.ppType} />
      <ListItemText primary={opt.label} slotProps={{ primary: { noWrap: true } }} sx={{ ml: 1 }} />
    </MenuItem>
  );

  const renderValue = (selected: string[]) => {
    if (selected.length === 0) {
      return <Box component="span" sx={{ color: "text.secondary" }}>{placeholder}</Box>;
    }
    const [first, ...rest] = selected;
    const firstOpt = optByValue.get(first);
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
        <Chip
          size="small"
          sx={{ minWidth: 0 }}
          label={
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
              {firstOpt && <Logo ppType={firstOpt.ppType} />}
              <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>{firstOpt?.label ?? first}</Box>
            </Box>
          }
        />
        {rest.length > 0 && (
          <Box component="span" sx={{ whiteSpace: "nowrap", color: "text.secondary", flexShrink: 0 }}>
            +{rest.length}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <FormControl size="small" sx={{ width }}>
      <InputLabel id={labelId} shrink>{label}</InputLabel>
      <Select<string[]>
        labelId={labelId}
        multiple
        displayEmpty
        value={value}
        sx={clearable ? { "& .MuiSelect-select": { pr: "52px !important" } } : undefined}
        input={
          <OutlinedInput
            notched
            label={label}
            endAdornment={
              clearable ? (
                <IconButton
                  size="small"
                  aria-label="Clear"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange([]);
                  }}
                  sx={{ position: "absolute", right: 26, p: 0.25, color: "action.active" }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              ) : null
            }
          />
        }
        renderValue={renderValue}
        onChange={(e: SelectChangeEvent<string[]>) => {
          const v = e.target.value;
          onChange(typeof v === "string" ? v.split(",") : v);
        }}
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        MenuProps={{
          autoFocus: false,
          PaperProps: { sx: { maxHeight: 360, width: Math.max(width, 320) } },
        }}
      >
        <ListSubheader sx={{ p: 1, lineHeight: "normal", bgcolor: "background.paper" }}>
          <TextField
            size="small"
            fullWidth
            autoFocus
            placeholder={searchPlaceholder}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key !== "Escape") e.stopPropagation();
            }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
        </ListSubheader>
        {selectedOpts.map(renderOption)}
        {selectedOpts.length > 0 && restOpts.length > 0 && <Divider component="li" />}
        {restOpts.map(renderOption)}
        {filtered.length === 0 && (
          <MenuItem disabled>
            <ListItemText primary="No matches" />
          </MenuItem>
        )}
      </Select>
    </FormControl>
  );
}
